"use client";

import React, { useEffect, useState } from "react";
import { Button } from "../../atoms/button/button";
import { CHANNEL_LINEUP_TOP_ANCHOR_ID } from "./scroll-to-top.shared";
import styles from "./scroll-to-top.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * PG4 — "a scroll to top button appears once the user has scrolled down, and takes them back to the
 * top." Its own small client leaf rather than a client root, on the argument
 * `directv-design-system/CLAUDE.md` names third: it needs runtime measurement (whether the page has
 * scrolled away from `ANCHOR_ID` is not knowable before layout) and no native element carries that
 * state.
 */

function reducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export interface ScrollToTopProps {
  /** PG4 — the control's text; the caller draws no control without one. */
  label: string;
}

function ScrollToTopImpl({ label }: ScrollToTopProps) {
  const [away, setAway] = useState(false);

  useEffect(() => {
    const anchor = document.getElementById(CHANNEL_LINEUP_TOP_ANCHOR_ID);
    /* Guarded because jsdom ships no IntersectionObserver, so a consumer's test would throw on
       mount. Not a browser-support guard. */
    if (!anchor || typeof IntersectionObserver !== "function") {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => setAway(entries.some(entry => !entry.isIntersecting)),
      { threshold: 0 }
    );
    observer.observe(anchor);

    return () => observer.disconnect();
  }, []);

  const toTop = () => {
    window.scrollTo({
      top: 0,
      behavior: reducedMotion() ? "auto" : "smooth"
    });
  };

  if (!away) {
    return null;
  }

  return (
    <div className={styles.scrollToTop}>
      {/* Visible wording IS the accessible name here; an ariaLabel equal to
          it would only duplicate the announcement (DDS CLAUDE.md). */}
      {/* `medium` is 48px, which clears this system's 44px floor. */}
      <Button onClick={toTop} size="medium" variant="secondary">
        {label}
      </Button>
    </div>
  );
}
export const ScrollToTop = withSafeRender(ScrollToTopImpl, "ScrollToTop");

export default ScrollToTop;
